<template>
  <FlexLayout v-if="displaySuggestions.length" align="center" :gap="8">
    <p v-if="label" class="text-sub text-gray-80 flex-shrink-0">{{ label }}</p>
    <div class="suggestion-tags">
      <div ref="suggestionListRef" class="suggestion-list">
        <div v-for="(tag, index) in displaySuggestions" :key="index" class="tag-item" @click="$emit('add', tag)">
          {{ tag.name }}
          <i class="el-icon-plus font-bold" />
        </div>
      </div>

      <div v-if="!isAtStart || !isAtEnd" class="controls">
        <button :class="{ disabled: isAtStart }" :disabled="isAtStart" @click="onPrev">
          <MaterialIcon>chevron_left</MaterialIcon>
        </button>
        <button :class="{ disabled: isAtEnd }" :disabled="isAtEnd" @click="onNext">
          <MaterialIcon>chevron_right</MaterialIcon>
        </button>
      </div>
    </div>
  </FlexLayout>
</template>

<script>
import { computed, defineComponent, ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
export default defineComponent({
  name: 'SuggestionTags',
  props: {
    label: {
      type: String,
      default: '',
    },
    selected: {
      type: Array,
      default: () => [],
    },
    suggestions: {
      type: Array,
      default: () => [],
    },
  },
  setup (props) {
    const suggestionListRef = ref(null)
    const isAtStart = ref(true)
    const isAtEnd = ref(false)
    const displaySuggestions = computed(() => {
      return props.suggestions.filter(tag => !props.selected.includes(tag.id))
    })

    const updateBounds = () => {
      const el = suggestionListRef.value
      if (!el) return
      // Use a small epsilon because scrollLeft can be fractional
      const eps = 1
      isAtStart.value = el.scrollLeft <= eps
      isAtEnd.value = el.scrollLeft + el.offsetWidth >= el.scrollWidth - eps
    }

    const onPrev = () => {
      const scrollArea = suggestionListRef.value
      if (scrollArea && !isAtStart.value) {
        scrollArea.scrollBy({
          left: -scrollArea.offsetWidth,
          behavior: 'smooth',
        })
      }
    }
    const onNext = () => {
      const scrollArea = suggestionListRef.value
      if (scrollArea && !isAtEnd.value) {
        scrollArea.scrollBy({
          left: scrollArea.offsetWidth,
          behavior: 'smooth',
        })
      }
    }

    // Attach listeners to keep track of scroll position
    const onScroll = () => {
      updateBounds()
    }

    // Watch for element being mounted and add event listener
    const register = () => {
      const el = suggestionListRef.value
      if (!el) return
      // initial state
      updateBounds()
      el.addEventListener('scroll', onScroll, { passive: true })
      // also update on resize to catch layout changes
      window.addEventListener('resize', updateBounds)
    }

    const unregister = () => {
      const el = suggestionListRef.value
      if (!el) return
      el.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', updateBounds)
    }

    watch(displaySuggestions, async () => {
      // Reset scroll position when suggestions change
      await nextTick()
      updateBounds()
    })

    onMounted(() => {
      register()
    })

    onBeforeUnmount(() => {
      unregister()
    })

    return {
      onPrev,
      onNext,
      suggestionListRef,
      displaySuggestions,
      isAtStart,
      isAtEnd,
    }
  },
})
</script>

<style lang="postcss" scoped>
.suggestion-tags {
  @apply w-full;
  @apply flex items-center justify-between gap-[8px];
}
.suggestion-list {
  @apply flex items-center overflow-x-hidden gap-[8px];
  @apply flex-1;
}

.tag-item {
  @apply flex-shrink-0 cursor-pointer;
  @apply text-primary-100 bg-primary-30 text-sm;
  @apply border-[1px] border-solid border-primary-100 rounded-[6px];
  @apply px-[8px] py-[5px];
}
.controls {
  @apply flex items-center gap-[8px];
}

.disabled {
  @apply opacity-30 cursor-not-allowed;
}
</style>
